import { CommonModule } from '@angular/common';
import { Component, computed, HostListener, inject, ViewChild } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { environment } from 'src/environments/environment';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { AuthService } from '../../services/auth/auth.service';
import { AnnouncementsService } from '../../services/communication/announcements.service';
import { RouterService } from '../../services/router/router.service';
import { SideMenuComponent } from '../side-menu/side-menu.component';

export interface Learner {
  id: string;
  name: string;
  colour: string;
  picture: string;
  notification?: boolean;
}

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    MatSidenavModule,
    MatButtonModule,
    MatIconModule,
    SideMenuComponent,
    CommonModule,
    RouterLinkActive,
    MatBadgeModule,
    MatButtonModule,
    IconComponent,
  ],
})
export class NavbarComponent {
  @ViewChild('drawer') drawer!: MatDrawer;
  private authService = inject(AuthService);
  private router = inject(Router);
  private announcementService = inject(AnnouncementsService);
  private routerService = inject(RouterService);

  showNav = computed(() => {
    // Access the currentRoute signal to create a dependency without an unused variable
    this.routerService.currentUrl(); // This ensures the computed reruns when routes change

    const currentPath = this.router.url.split('?')[0];

    if (currentPath === '/inbox/announcements') {
      return true;
    }

    if (currentPath === '/enrolments') {
      return false;
    }

    if (currentPath === '/unauthorised') {
      return false;
    }

    const pathSegments = currentPath.split('/').filter((segment) => segment !== '');
    const isTopLevel = pathSegments.length <= 1;

    const isAuthenticated = this.authService.isAuthenticated();
    return isTopLevel && (isAuthenticated === true || environment.production === false);
  });

  showFiller = false;
  unreadCount = 0;
  isOpen = false;
  loading = false;
  isMenuOpen = false;
  unreadGroupCount = 0;
  totalUnreadCount = this.announcementService.serverUnreadCount;

  toggleDrawer() {
    this.isOpen = !this.isOpen;

    if (this.drawer) {
      this.loading = true;
      this.drawer.toggle();
    }
  }

  closeDrawer() {
    this.isOpen = false;
    if (this.drawer) {
      this.drawer.close();
    }
  }

  handleCloseEvent() {
    this.closeDrawer();
  }

  isActive(url: string): boolean {
    return this.router.url === url;
  }

  isActiveRoute(): boolean {
    const routes = ['/help', '/manage-family', '/payments', '/enrolments', '/guardians'];
    return routes.some((route) => this.router.url.includes(route));
  }

  navigateToEvents() {
    if (this.router.url === '/events') {
      // Already on the events page, scroll to the calendar section
      const eventsPageComponent = document.querySelector('.events-page');
      if (eventsPageComponent) {
        // You may need to use a service or an event emitter to trigger the scrollToIndex method directly
        const scrollToIndexFunction = (window as any).scrollToIndex;
        if (scrollToIndexFunction) {
          scrollToIndexFunction(0);
        }
      }
    } else {
      // Navigate to the events page
      this.router.navigate(['/events']);
    }
  }

  isKeyboardVisible = false;

  private readonly initialInnerHeight: number = window.innerHeight;

  @HostListener('window:resize')
  onResize(): void {
    this.isKeyboardVisible = window.innerHeight < this.initialInnerHeight;
  }
}
